import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, TextField, Typography, InputAdornment, CircularProgress } from '@mui/material';
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';

/* ── Inline SVG educational icons ── */
const EduIcons = {
  cap: (s) => (
    <svg width={s} height={s} viewBox="0 0 64 64" fill="none">
      <polygon points="32,8 62,24 32,40 2,24" fill="rgba(150,185,255,0.9)"/>
      <path d="M14 30v16c0 0 8 10 18 10s18-10 18-10V30" fill="rgba(150,185,255,0.55)"/>
      <line x1="62" y1="24" x2="62" y2="42" stroke="rgba(150,185,255,0.8)" strokeWidth="3" strokeLinecap="round"/>
      <circle cx="62" cy="44" r="3" fill="rgba(150,185,255,0.8)"/>
    </svg>
  ),
  book: (s) => (
    <svg width={s} height={s} viewBox="0 0 64 64" fill="none">
      <rect x="4" y="8" width="26" height="48" rx="3" fill="rgba(150,185,255,0.75)"/>
      <rect x="34" y="8" width="26" height="48" rx="3" fill="rgba(150,185,255,0.55)"/>
      <line x1="30" y1="8" x2="30" y2="56" stroke="rgba(150,185,255,0.4)" strokeWidth="2"/>
      <line x1="10" y1="20" x2="24" y2="20" stroke="rgba(10,21,48,0.35)" strokeWidth="2" strokeLinecap="round"/>
      <line x1="10" y1="28" x2="24" y2="28" stroke="rgba(10,21,48,0.35)" strokeWidth="2" strokeLinecap="round"/>
      <line x1="10" y1="36" x2="20" y2="36" stroke="rgba(10,21,48,0.35)" strokeWidth="2" strokeLinecap="round"/>
      <line x1="40" y1="20" x2="54" y2="20" stroke="rgba(10,21,48,0.25)" strokeWidth="2" strokeLinecap="round"/>
      <line x1="40" y1="28" x2="54" y2="28" stroke="rgba(10,21,48,0.25)" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  ),
  atom: (s) => (
    <svg width={s} height={s} viewBox="0 0 64 64" fill="none">
      <ellipse cx="32" cy="32" rx="28" ry="10" stroke="rgba(150,185,255,0.85)" strokeWidth="2" fill="none"/>
      <ellipse cx="32" cy="32" rx="28" ry="10" stroke="rgba(150,185,255,0.65)" strokeWidth="2" fill="none" transform="rotate(60 32 32)"/>
      <ellipse cx="32" cy="32" rx="28" ry="10" stroke="rgba(150,185,255,0.65)" strokeWidth="2" fill="none" transform="rotate(120 32 32)"/>
      <circle cx="32" cy="32" r="5" fill="rgba(150,185,255,0.95)"/>
    </svg>
  ),
  pencil: (s) => (
    <svg width={s} height={s * 2} viewBox="0 0 32 64" fill="none">
      <rect x="8" y="8" width="16" height="40" rx="2" fill="rgba(150,185,255,0.8)"/>
      <polygon points="8,48 24,48 16,62" fill="rgba(150,185,255,0.65)"/>
      <rect x="8" y="6" width="16" height="6" rx="1" fill="rgba(150,185,255,0.5)"/>
      <line x1="16" y1="10" x2="16" y2="48" stroke="rgba(10,21,48,0.15)" strokeWidth="1"/>
    </svg>
  ),
  trophy: (s) => (
    <svg width={s} height={s} viewBox="0 0 64 64" fill="none">
      <path d="M20 8h24v20a12 12 0 01-24 0V8z" fill="rgba(150,185,255,0.85)"/>
      <path d="M8 10h12v12a6 6 0 01-12 0V10z" fill="rgba(150,185,255,0.5)"/>
      <path d="M44 10h12v12a6 6 0 01-12 0V10z" fill="rgba(150,185,255,0.5)"/>
      <rect x="28" y="40" width="8" height="10" fill="rgba(150,185,255,0.65)"/>
      <rect x="18" y="50" width="28" height="6" rx="2" fill="rgba(150,185,255,0.75)"/>
    </svg>
  ),
  bulb: (s) => (
    <svg width={s} height={s} viewBox="0 0 64 64" fill="none">
      <path d="M32 6a18 18 0 0110 33l-2 4H24l-2-4A18 18 0 0132 6z" fill="rgba(150,185,255,0.8)"/>
      <rect x="24" y="44" width="16" height="4" rx="2" fill="rgba(150,185,255,0.6)"/>
      <rect x="26" y="50" width="12" height="4" rx="2" fill="rgba(150,185,255,0.5)"/>
      <line x1="32" y1="2" x2="32" y2="5" stroke="rgba(150,185,255,0.7)" strokeWidth="2" strokeLinecap="round"/>
      <line x1="52" y1="10" x2="49" y2="13" stroke="rgba(150,185,255,0.7)" strokeWidth="2" strokeLinecap="round"/>
      <line x1="12" y1="10" x2="15" y2="13" stroke="rgba(150,185,255,0.7)" strokeWidth="2" strokeLinecap="round"/>
      <line x1="57" y1="28" x2="53" y2="28" stroke="rgba(150,185,255,0.7)" strokeWidth="2" strokeLinecap="round"/>
      <line x1="7" y1="28" x2="11" y2="28" stroke="rgba(150,185,255,0.7)" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  ),
  calc: (s) => (
    <svg width={s} height={s * 1.3} viewBox="0 0 48 64" fill="none">
      <rect x="4" y="2" width="40" height="60" rx="5" fill="rgba(150,185,255,0.72)"/>
      <rect x="10" y="8" width="28" height="14" rx="2" fill="rgba(10,21,48,0.28)"/>
      {[36, 48].map(y => [16, 24, 32].map(x => (
        <circle key={`${x}${y}`} cx={x} cy={y} r="3.5" fill="rgba(10,21,48,0.35)"/>
      )))}
    </svg>
  ),
  ruler: (s) => (
    <svg width={s * 0.55} height={s * 2.2} viewBox="0 0 28 72" fill="none">
      <rect x="4" y="2" width="20" height="68" rx="3" fill="rgba(150,185,255,0.72)"/>
      {[10, 18, 26, 34, 42, 50, 58].map((y, i) => (
        <line key={y} x1="4" y1={y} x2={i % 2 === 0 ? "18" : "13"} y2={y}
          stroke="rgba(10,21,48,0.38)" strokeWidth="1.5" strokeLinecap="round"/>
      ))}
    </svg>
  ),
};

const floatingItems = [
  { top: '6%',  left: '4%',   icon: 'cap',    size: 54, opacity: 0.18, rotate: '-15deg', delay: '0s'   },
  { top: '4%',  right: '6%',  icon: 'book',   size: 50, opacity: 0.15, rotate: '12deg',  delay: '0.4s' },
  { top: '36%', left: '2%',   icon: 'pencil', size: 30, opacity: 0.14, rotate: '-30deg', delay: '0.8s' },
  { top: '40%', right: '3%',  icon: 'atom',   size: 46, opacity: 0.14, rotate: '20deg',  delay: '0.2s' },
  { bottom:'9%',left: '5%',   icon: 'calc',   size: 36, opacity: 0.13, rotate: '8deg',   delay: '1s'   },
  { bottom:'7%',right: '5%',  icon: 'trophy', size: 44, opacity: 0.16, rotate: '-10deg', delay: '0.6s' },
  { top: '17%', right: '14%', icon: 'bulb',   size: 36, opacity: 0.12, rotate: '-8deg',  delay: '0.3s' },
  { bottom:'22%',left:'12%',  icon: 'ruler',  size: 28, opacity: 0.11, rotate: '35deg',  delay: '1.2s' },
];

const AccountEntry = () => {
  const [accountName, setAccountName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const handleNext = async () => {
    const trimmedName = accountName.trim().toLowerCase();
    if (!trimmedName) { setError('Please enter an account name'); return; }
    setLoading(true); setError('');

    // ── Static: waralsadmin ──
    if (trimmedName === 'waralsadmin') {
      localStorage.setItem('accountEntryToken', 'static-warals-token');
      navigate('/login', { state: { accountName: 'waralsadmin', accessToken: 'static-warals-token' } });
      setLoading(false); return;
    }

    // ── Static: studentpanel ──
    if (trimmedName === 'studentpanel') {
      localStorage.setItem('accountEntryToken', 'static-student-token');
      navigate('/login', { state: { accountName: 'studentpanel', accessToken: 'static-student-token' } });
      setLoading(false); return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/core/SelectClient/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ client_code: trimmedName }),
      });
      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('accountEntryToken', data.token);
        navigate('/login', { state: { accountName: trimmedName, accessToken: data.token } });
        return;
      }

      // ── Static: teacherpanel (fallback after API miss) ──
      if (trimmedName === 'teacherpanel') {
        localStorage.setItem('accountEntryToken', 'static-teacher-token');
        navigate('/login', { state: { accountName: 'teacherpanel', accessToken: 'static-teacher-token' } });
        setLoading(false); return;
      }

      throw new Error('Not found');
    } catch {
      setError('Account not found. Please try another name or contact support.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700&display=swap');
        @keyframes floatUpDown {
          0%,100% { transform: translateY(0px) rotate(var(--rot)); }
          50%      { transform: translateY(-16px) rotate(var(--rot)); }
        }
        .edu-float {
          position: absolute;
          pointer-events: none;
          animation: floatUpDown 6s ease-in-out infinite;
          animation-delay: var(--delay);
          filter: drop-shadow(0 6px 18px rgba(78,115,223,0.28));
        }
      `}</style>

      <Box sx={{
        minHeight: '100vh',
        background: 'linear-gradient(160deg, #0d1b4b 0%, #101d3e 45%, #0a1530 100%)',
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        padding: 2, position: 'relative', overflow: 'hidden',
        '&::before': {
          content: '""', position: 'absolute', width: '620px', height: '620px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(78,115,223,0.14) 0%, transparent 70%)',
          top: '-160px', right: '-160px', pointerEvents: 'none',
        },
        '&::after': {
          content: '""', position: 'absolute', width: '520px', height: '520px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(34,74,190,0.11) 0%, transparent 70%)',
          bottom: '-120px', left: '-120px', pointerEvents: 'none',
        },
      }}>

        {/* Dot grid texture */}
        <Box sx={{
          position: 'absolute', inset: 0,
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.055) 1px, transparent 1px)',
          backgroundSize: '38px 38px', pointerEvents: 'none',
        }} />

        {/* Floating educational icons */}
        {floatingItems.map((item, i) => (
          <div key={i} className="edu-float" style={{
            top: item.top, left: item.left, right: item.right, bottom: item.bottom,
            opacity: item.opacity, '--rot': item.rotate, '--delay': item.delay,
          }}>
            {EduIcons[item.icon](item.size)}
          </div>
        ))}

        {/* Card */}
        <Box sx={{
          position: 'relative', zIndex: 2,
          background: 'rgba(255,255,255,0.045)',
          backdropFilter: 'blur(28px)', WebkitBackdropFilter: 'blur(28px)',
          border: '1px solid rgba(255,255,255,0.10)',
          padding: { xs: '36px 28px', sm: '48px 44px' },
          borderRadius: '24px',
          boxShadow: '0 32px 80px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.05) inset',
          width: '100%', maxWidth: 440,
          transition: 'transform 0.3s ease, box-shadow 0.3s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 40px 90px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.08) inset',
          },
        }}>

          {/* Logo */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <img src={require('../../assets/warals-logo.png')} alt="WARALS Logo"
              style={{ height: 'auto', width: '155px', maxWidth: '100%', filter: 'brightness(0) invert(1)', opacity: 0.92 }}/>
          </Box>

          <Box sx={{ height: '1px', background: 'linear-gradient(to right, transparent, rgba(255,255,255,0.15), transparent)', mb: 3 }} />

          {/* Institute badge */}
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1.2, mb: 3,
            px: 2, py: 1.2, borderRadius: '10px',
            background: 'rgba(91,141,238,0.10)',
            border: '1px solid rgba(91,141,238,0.22)',
          }}>
            <svg width="20" height="20" viewBox="0 0 64 64" fill="none">
              <rect x="8" y="28" width="48" height="30" rx="3" fill="rgba(150,185,255,0.85)"/>
              <polygon points="32,4 60,24 4,24" fill="rgba(150,185,255,0.65)"/>
              <rect x="24" y="40" width="16" height="18" rx="2" fill="rgba(10,21,48,0.3)"/>
              <rect x="12" y="34" width="10" height="10" rx="1" fill="rgba(10,21,48,0.22)"/>
              <rect x="42" y="34" width="10" height="10" rx="1" fill="rgba(10,21,48,0.22)"/>
            </svg>
            <Typography sx={{ color: 'rgba(180,210,255,0.85)', fontSize: '0.78rem', fontFamily: "'Sora', sans-serif", fontWeight: 500, letterSpacing: '0.03em' }}>
              WARALS Institute Management System
            </Typography>
          </Box>

          {/* Heading */}
          <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700, letterSpacing: '-0.3px', mb: 0.5, fontFamily: "'Sora', sans-serif" }}>
            Welcome
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.45)', mb: 3, fontSize: '0.85rem', fontFamily: "'Sora', sans-serif" }}>
            Enter your account name to continue to WIMS
          </Typography>

          {/* Input */}
          <TextField fullWidth placeholder="Account name"
            value={accountName} error={!!error} helperText={error}
            onChange={(e) => { setAccountName(e.target.value); setError(''); }}
            onKeyPress={(e) => e.key === 'Enter' && handleNext()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <BusinessCenterOutlinedIcon sx={{ color: error ? '#f87171' : 'rgba(255,255,255,0.35)', fontSize: 20 }}/>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px', backgroundColor: 'rgba(255,255,255,0.06)',
                color: '#fff', fontSize: '0.95rem', fontFamily: "'Sora', sans-serif",
                transition: 'background 0.2s ease',
                '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
                '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.25)' },
                '&.Mui-focused': { backgroundColor: 'rgba(255,255,255,0.09)' },
                '&.Mui-focused fieldset': { borderColor: '#5b8dee', borderWidth: '1.5px' },
                '&.Mui-error fieldset': { borderColor: 'rgba(248,113,113,0.6)' },
              },
              '& input': { color: '#fff' },
              '& input::placeholder': { color: 'rgba(255,255,255,0.3)', opacity: 1 },
              '& .MuiFormHelperText-root': { color: '#f87171', fontSize: '0.78rem', mt: 0.75, fontFamily: "'Sora', sans-serif" },
            }}
          />

          {/* Button */}
          <Button fullWidth variant="contained" disabled={loading} onClick={handleNext}
            sx={{
              mt: 2.5, py: 1.6, borderRadius: '12px',
              background: 'linear-gradient(135deg, #5b8dee 0%, #3b5bdb 50%, #2246c4 100%)',
              textTransform: 'none', fontSize: '0.97rem', fontWeight: 600,
              fontFamily: "'Sora', sans-serif", letterSpacing: '0.02em',
              boxShadow: '0 8px 24px rgba(59,91,219,0.45)',
              border: '1px solid rgba(255,255,255,0.12)',
              position: 'relative', overflow: 'hidden',
              '&::before': {
                content: '""', position: 'absolute', top: 0, left: '-100%',
                width: '100%', height: '100%',
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)',
                transition: 'left 0.5s ease',
              },
              '&:hover::before': { left: '100%' },
              '&:hover': {
                background: 'linear-gradient(135deg, #6b9bf0 0%, #4a6ae8 50%, #2d55d4 100%)',
                boxShadow: '0 12px 30px rgba(59,91,219,0.55)', transform: 'translateY(-1px)',
              },
              '&:active': { transform: 'translateY(0)' },
              '&.Mui-disabled': { background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.3)' },
              transition: 'all 0.2s ease',
            }}>
            {loading ? <CircularProgress size={20} sx={{ color: 'rgba(255,255,255,0.5)' }}/> : 'Continue'}
          </Button>

          <Box sx={{ height: '1px', background: 'linear-gradient(to right, transparent, rgba(255,255,255,0.1), transparent)', mt: 2.5, mb: 2 }} />

          <Typography variant="body2" align="center" sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.83rem', fontFamily: "'Sora', sans-serif" }}>
            Don't have an account?{' '}
            <a href="#" style={{ color: '#7aa7f5', fontWeight: 600, textDecoration: 'none' }}
              onMouseEnter={(e) => (e.target.style.color = '#a0c0ff')}
              onMouseLeave={(e) => (e.target.style.color = '#7aa7f5')}>Sign Up</a>
          </Typography>
        </Box>
      </Box>
    </>
  );
};

export default AccountEntry;